<template>
  <v-card 
    class="benefit-card"
    :class="`benefit-card--${color}`"
    elevation="0"
    :style="{ animationDelay: `${delay}ms` }"
  >
    <v-card-text class="pa-6">
      <div class="benefit-icon-wrapper mb-4">
        <v-icon 
          :color="color" 
          size="48"
          class="benefit-icon"
        >
          {{ icon }}
        </v-icon>
      </div>
      
      <div class="benefit-value mb-2" ref="valueRef">
        {{ animatedValue }}
      </div>
      
      <h3 class="benefit-title mb-2">{{ title }}</h3>
      <p class="benefit-description">{{ description }}</p>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'BenefitCard',
  
  props: {
    icon: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    color: {
      type: String,
      default: 'primary'
    },
    delay: {
      type: Number,
      default: 0
    }
  },
  
  data() {
    return {
      animatedValue: '0',
      isAnimated: false
    };
  },
  
  mounted() {
    this.observeCard();
  },
  
  methods: {
    observeCard() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isAnimated) {
              this.animateValue();
              this.isAnimated = true;
            }
          });
        },
        { threshold: 0.5 }
      );
      
      observer.observe(this.$el);
    },
    
    animateValue() {
      // If value is a percentage, animate the number
      if (this.value.includes('%')) {
        const targetValue = parseInt(this.value);
        const duration = 2000;
        const step = targetValue / (duration / 16);
        let current = 0;
        
        const timer = setInterval(() => {
          current += step;
          if (current >= targetValue) {
            current = targetValue;
            clearInterval(timer);
          }
          this.animatedValue = Math.round(current) + '%';
        }, 16);
      } else {
        // For non-numeric values, just show them
        this.animatedValue = this.value;
      }
    }
  }
};
</script>

<style scoped>
.benefit-card {
  height: 100%;
  border-radius: 16px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.benefit-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--gradient-start), var(--gradient-end));
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}

.benefit-card:hover::before {
  transform: scaleX(1);
}

.benefit-card--primary {
  --gradient-start: #667eea;
  --gradient-end: #764ba2;
}

.benefit-card--success {
  --gradient-start: #56ab2f;
  --gradient-end: #a8e063;
}

.benefit-card--warning {
  --gradient-start: #f2994a;
  --gradient-end: #f2c94c;
}

.benefit-card--info {
  --gradient-start: #2193b0;
  --gradient-end: #6dd5ed;
}

.benefit-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.benefit-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  position: relative;
}

.benefit-icon-wrapper::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: inherit;
  opacity: 0.2;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.2);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0.2;
  }
}

.benefit-icon {
  color: white !important;
  z-index: 1;
}

.benefit-value {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  line-height: 1;
}

.benefit-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
}

.benefit-description {
  font-size: 1rem;
  color: #546e7a;
  text-align: center;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .benefit-value {
    font-size: 2.5rem;
  }
  
  .benefit-title {
    font-size: 1.2rem;
  }
  
  .benefit-description {
    font-size: 0.9rem;
  }
}
</style> 